import React, { useMemo, useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { useAuth0 } from '@auth0/auth0-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faBuilding, faTable, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import AddFloorModal from '../modals/AddFloorModal';
import EditFloorModal from '../modals/EditFloorModal';
import AdminPageHeader from '../shared/AdminPageHeader';
import AdminNotice from '../shared/AdminNotice';
import AdminEmptyState from '../shared/AdminEmptyState';
import AdminSectionCard from '../shared/AdminSectionCard';
import '../../styles/route_pages/Floors.css';
import { addFloor, deleteFloor, updateFloor, reorderFloors } from '../../utils/floorService';
import { useVenueId } from '../../hooks/useUserData';
import LoadingSpinner from '../LoadingSpinner';

const Floors = () => {
  const venueId = useVenueId();
  const { floors, setFloors, tables } = useAdmin();
  const { getAccessTokenSilently } = useAuth0();
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentFloor, setCurrentFloor] = useState(null);
  const [warning, setWarning] = useState('');
  const [loading, setLoading] = useState(false);

  const sortedFloors = useMemo(() => [...floors].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)), [floors]);

  const handleFloorAdded = async (newFloor) => {
    try {
      const addedFloor = await addFloor(venueId, { ...newFloor, venueId: parseInt(venueId, 10) }, getAccessTokenSilently);
      setFloors([...floors, addedFloor]);
      setIsAddModalVisible(false);
    } catch (error) {
      console.error('Failed to add floor:', error);
    }
  };

  const handleFloorUpdated = async (updatedFloor) => {
    try {
      const updated = await updateFloor(venueId, updatedFloor, getAccessTokenSilently);
      setFloors(floors.map((floor) => (floor.id === updated.id ? updated : floor)));
      setIsEditModalVisible(false);
    } catch (error) {
      console.error('Failed to update floor:', error);
    }
  };

  const handleFloorDeleted = async (floorId) => {
    const tablesForFloor = tables.filter((table) => table.floorId === floorId);
    if (tablesForFloor.length > 0) {
      setWarning('Katı silmek için önce bu kattaki tüm masaları kaldırmalısınız.');
      return;
    }

    try {
      await deleteFloor(venueId, floorId, getAccessTokenSilently);
      setFloors(floors.filter((floor) => floor.id !== floorId));
    } catch (error) {
      console.error('Failed to delete floor:', error);
    }
  };

  const getTableCount = (floorId) => tables.filter((table) => table.floorId === floorId).length;

  const handleMoveFloor = async (floorToMove, direction) => {
    if (loading) {
      return;
    }

    const currentIndex = sortedFloors.findIndex((floor) => floor.id === floorToMove.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= sortedFloors.length) {
      return;
    }

    try {
      setLoading(true);
      const reorderedFloors = Array.from(sortedFloors);
      [reorderedFloors[currentIndex], reorderedFloors[newIndex]] = [reorderedFloors[newIndex], reorderedFloors[currentIndex]];
      const floorsWithNewOrder = reorderedFloors.map((floor, index) => ({ id: floor.id, sortOrder: index }));
      await reorderFloors(venueId, floorsWithNewOrder, getAccessTokenSilently);

      const updatedFloors = floors.map((floor) => {
        const newOrder = floorsWithNewOrder.find((entry) => entry.id === floor.id);
        return newOrder ? { ...floor, sortOrder: newOrder.sortOrder } : floor;
      });

      setFloors(updatedFloors);
    } catch (error) {
      console.error('Failed to move floor:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <AdminPageHeader
        title="Katlar"
        subtitle="Masa yerleşimini kat bazında gruplayın ve kat sırasını daha okunur bir operasyon listesiyle koruyun."
        actions={(
          <button className="btn btn-primary" onClick={() => setIsAddModalVisible(true)}>
            <FontAwesomeIcon icon={faPlus} />
            Yeni Kat
          </button>
        )}
      />

      {loading ? <LoadingSpinner /> : null}
      {warning ? <AdminNotice tone="warning" title="Kat silinemedi" message={warning} onDismiss={() => setWarning('')} /> : null}

      <div className="admin-page-stack">
        <AdminSectionCard>
          {sortedFloors.length === 0 ? (
            <AdminEmptyState
              icon={faBuilding}
              title="Henüz kat bulunmuyor"
              description="Venue’nüze ilk katı ekleyerek masa düzenini yapılandırmaya başlayın."
              action={(
                <button className="btn btn-primary" onClick={() => setIsAddModalVisible(true)}>
                  <FontAwesomeIcon icon={faPlus} />
                  İlk Katı Ekle
                </button>
              )}
            />
          ) : (
            <div className="admin-management-list floors-management-list">
              {sortedFloors.map((floor, index) => (
                <article key={floor.id} className="admin-management-row floors-row">
                    <div className="admin-management-leading">
                      <div className="admin-symbol-token floors-token">
                        <FontAwesomeIcon icon={faBuilding} />
                      </div>
                      <div className="admin-management-main">
                        <h3 className="admin-management-title">{floor.name}</h3>
                        <div className="admin-chip-row">
                          <span className="admin-chip admin-chip-accent">
                            <FontAwesomeIcon icon={faTable} />
                          {getTableCount(floor.id)} masa
                        </span>
                        <span className="admin-chip admin-chip-muted">Sıra #{index + 1}</span>
                      </div>
                    </div>
                  </div>

                  <div className="admin-management-meta">
                    <div className="admin-stat-stack">
                      <div className="admin-stat-card">
                        <strong>{getTableCount(floor.id)}</strong>
                        <span>Bağlı masa</span>
                      </div>
                      <div className="admin-stat-card">
                        <strong>{index + 1}</strong>
                        <span>Kat sırası</span>
                      </div>
                    </div>
                  </div>

                  <div className="admin-management-actions">
                    <div className="admin-reorder-cluster" aria-label={`${floor.name} sıralama kontrolleri`}>
                      <div className="admin-reorder-buttons">
                        <button
                          type="button"
                          className="admin-reorder-button"
                          onClick={() => handleMoveFloor(floor, 'up')}
                          disabled={loading || index === 0}
                          title="Katı yukarı taşı"
                          aria-label={`${floor.name} katını yukarı taşı`}
                        >
                          <FontAwesomeIcon icon={faChevronUp} />
                        </button>
                        <button
                          type="button"
                          className="admin-reorder-button"
                          onClick={() => handleMoveFloor(floor, 'down')}
                          disabled={loading || index === sortedFloors.length - 1}
                          title="Katı aşağı taşı"
                          aria-label={`${floor.name} katını aşağı taşı`}
                        >
                          <FontAwesomeIcon icon={faChevronDown} />
                        </button>
                      </div>
                    </div>

                    <div className="admin-action-cluster">
                      <button type="button" className="btn btn-secondary" onClick={() => { setCurrentFloor(floor); setIsEditModalVisible(true); }}>
                        <FontAwesomeIcon icon={faEdit} />
                        Düzenle
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </AdminSectionCard>
      </div>

      <AddFloorModal show={isAddModalVisible} onClose={() => setIsAddModalVisible(false)} onFloorAdded={handleFloorAdded} />
      {currentFloor ? (
        <EditFloorModal
          show={isEditModalVisible}
          onClose={() => setIsEditModalVisible(false)}
          floor={currentFloor}
          onFloorUpdated={handleFloorUpdated}
          onFloorDeleted={handleFloorDeleted}
          tableCount={getTableCount(currentFloor.id)}
        />
      ) : null}
    </div>
  );
};

export default Floors;
